import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalculateIcon from '@mui/icons-material/Calculate';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/Security';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const featureCategories = [
  {
    title: 'Time Tracking',
    icon: <AccessTimeIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    description: 'Comprehensive timesheet management for all your contractors.',
    features: [
      'Weekly timesheet views',
      'Configurable week-ending day (Friday, Saturday, Sunday)',
      'Daily hour entry with validation',
      'Overtime tracking and calculations',
      'Bulk time entry for efficiency',
    ],
  },
  {
    title: 'Contractor Management',
    icon: <PeopleIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    description: 'Easily manage all your 1099 contractors in one place.',
    features: [
      'Unlimited contractor profiles',
      'Store contact information',
      'Track hourly rates per contractor',
      'Maintain payment history',
      'Active/inactive status management',
    ],
  },
  {
    title: 'Pay Calculations',
    icon: <CalculateIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    description: 'Automatic gross pay calculations based on hours worked.',
    features: [
      'Real-time pay calculations',
      'Hours × Rate = Gross Pay formula',
      'Weekly and period totals',
      'Year-to-date earnings tracking',
      'Payment summary reports',
    ],
  },
  {
    title: '1099 Tax Forms',
    icon: <DescriptionIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    description: 'Generate IRS-compliant 1099-MISC forms at tax time.',
    features: [
      'IRS-compliant 1099-MISC generation',
      '$600+ threshold tracking',
      'Download as PDF',
      'Employer copies included',
      'Year-by-year tax data',
    ],
  },
  {
    title: 'Reporting',
    icon: <BarChartIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    description:
      'Comprehensive reports to understand your contractor expenses.',
    features: [
      'Payment history reports',
      'Year-to-date summaries',
      'Contractor earnings breakdown',
      'Export data to CSV',
      'Filterable date ranges',
    ],
  },
  {
    title: 'Security',
    icon: <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
    description: 'Bank-level security to protect sensitive contractor data.',
    features: [
      'TIN/SSN encryption at rest',
      'Secure HTTPS connections',
      'JWT authentication',
      'Role-based access control',
      'Regular security audits',
    ],
  },
];

const additionalFeatures = [
  {
    icon: <CalendarMonthIcon />,
    title: 'Flexible Week Settings',
    description: 'Choose your week-ending day to match your pay periods.',
  },
  {
    icon: <CloudDownloadIcon />,
    title: 'Data Export',
    description: 'Export payments and reports to CSV for accounting software.',
  },
  {
    icon: <PaymentIcon />,
    title: 'Payment Methods',
    description: 'Track different payment methods (check, ACH, cash, etc.).',
  },
];

export default function FeaturesPage() {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          pt: 12,
          pb: 8,
          background: 'linear-gradient(135deg, #f8faf8 0%, #e8f5e9 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Chip label="Features" sx={{ mb: 2 }} color="primary" />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
              Powerful Features for Simply Payroll
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ lineHeight: 1.7 }}
            >
              SimplyPayroll is built specifically for tracking 1099 contractor
              payments. No bloat, no unnecessary features—just what you need to
              stay organized and compliant.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Feature Categories */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {featureCategories.map((category, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 3,
                        mb: 3,
                      }}
                    >
                      {category.icon}
                      <Box>
                        <Typography
                          variant="h4"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          {category.title}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {category.description}
                        </Typography>
                      </Box>
                    </Box>
                    <List dense>
                      {category.features.map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon
                              sx={{ color: 'primary.main', fontSize: 20 }}
                            />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Additional Features */}
      <Box sx={{ py: 10, bgcolor: '#f8faf8' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              And More...
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Additional features to make your contractor payroll management
              even easier.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {additionalFeatures.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 4,
                    bgcolor: 'white',
                    borderRadius: 3,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                      color: 'white',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* 1099 Compliance Section */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Tax Compliance"
                sx={{ mb: 2 }}
                color="primary"
                variant="outlined"
              />
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                Stay Compliant with IRS Requirements
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, lineHeight: 1.8 }}
              >
                SimplyPayroll automatically tracks the $600 threshold for
                1099-MISC reporting. When tax season comes, you'll have
                everything you need to generate accurate 1099 forms for your
                contractors.
              </Typography>
              <List>
                {[
                  'Automatic $600+ threshold tracking',
                  'IRS-compliant 1099-MISC format',
                  'Both recipient and employer copies',
                  'Year-by-year historical data',
                  'Secure TIN/EIN storage',
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: 'primary.main' }} />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 4,
                  p: 4,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Sample 1099-MISC Data
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography color="text.secondary">Tax Year</Typography>
                    <Typography fontWeight={600}>2024</Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography color="text.secondary">Contractor</Typography>
                    <Typography fontWeight={600}>John Smith</Typography>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography color="text.secondary">TIN</Typography>
                    <Typography fontWeight={600}>***-**-1234</Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: 1,
                      borderColor: 'divider',
                      pt: 2,
                    }}
                  >
                    <Typography fontWeight={600}>
                      Box 7 - Nonemployee Compensation
                    </Typography>
                    <Typography fontWeight={700} color="primary.main">
                      $45,000.00
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}
